import React, { useEffect, useMemo, useState } from "react";
import {
  FaCloudUploadAlt,
  FaImage,
  FaSave,
  FaTimes,
  FaTrashAlt,
  FaUpload,
} from "react-icons/fa";

export default function LogoUploadDrawer({
  isOpen,
  uploading,
  uploadProgress,
  currentLogoSrc,
  clubName,
  onClose,
  onSubmit,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [outputFormat, setOutputFormat] = useState("webp");

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFile(null);
    setFilePreview("");
    setOutputFormat("webp");
  }, [isOpen]);

  useEffect(() => {
    if (!selectedFile) {
      setFilePreview("");
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setFilePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const previewSrc = useMemo(() => {
    return filePreview || currentLogoSrc || "/img/no-image.jpg";
  }, [filePreview, currentLogoSrc]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const clearFile = () => {
    setSelectedFile(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    onSubmit?.({
      file: selectedFile,
      outputFormat,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Logo du club
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Changer le logo
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Sélectionnez un fichier image pour mettre à jour le logo{" "}
              {clubName ? `de ${clubName}` : "du club"}.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={uploading}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 p-5 text-center dark:border-slate-800 dark:bg-slate-950">
              <div className="mx-auto flex h-44 w-44 items-center justify-center overflow-hidden rounded-[32px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <img
                  src={previewSrc}
                  alt="Aperçu du logo"
                  className="h-full w-full object-contain"
                  onError={(event) => {
                    event.currentTarget.src = "/img/no-image.jpg";
                  }}
                />
              </div>

              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Aperçu du logo qui sera affiché dans l’application.
              </p>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
              <label className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                <FaUpload />
                Fichier image
              </label>

              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                onChange={handleFileChange}
                disabled={uploading}
                className="block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition file:mr-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-bold file:text-indigo-700 hover:file:bg-indigo-100 focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:file:bg-indigo-500/10 dark:file:text-indigo-300"
              />

              <div className="mt-3 flex items-center gap-2">
                <FaCloudUploadAlt className="text-slate-400" />

                <select
                  value={outputFormat}
                  onChange={(event) => setOutputFormat(event.target.value)}
                  disabled={uploading}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                >
                  <option value="webp">WEBP recommandé</option>
                  <option value="jpg">JPG</option>
                  <option value="png">PNG</option>
                </select>
              </div>

              {selectedFile ? (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                  <span className="min-w-0 truncate">
                    {selectedFile.name} — {Math.round(selectedFile.size / 1024)} Ko
                  </span>

                  <button
                    type="button"
                    onClick={clearFile}
                    disabled={uploading}
                    className="inline-flex items-center gap-1 rounded-xl bg-rose-50 px-2 py-1 font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:bg-rose-500/10 dark:text-rose-300"
                  >
                    <FaTrashAlt />
                    Retirer
                  </button>
                </div>
              ) : null}

              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Formats acceptés : PNG, JPG, WEBP, SVG. Taille maximale : 10 Mo.
                Pour un rendu propre, privilégiez un logo carré ou transparent.
              </p>

              {uploading && selectedFile ? (
                <div className="mt-4">
                  <div className="mb-1 flex items-center justify-between text-xs font-bold text-slate-600 dark:text-slate-300">
                    <span>Transfert en cours</span>
                    <span>{uploadProgress}%</span>
                  </div>

                  <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#013E8E] to-[#4CA82F] transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-[24px] border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-500/20 dark:bg-indigo-500/10">
              <div className="flex gap-3">
                <span className="mt-0.5 text-indigo-700 dark:text-indigo-300">
                  <FaImage />
                </span>

                <p className="text-sm leading-6 text-indigo-900 dark:text-indigo-100">
                  Le logo est envoyé sur le serveur puis l’URL est enregistrée
                  dans les paramètres du club. Pensez ensuite à vérifier
                  l’aperçu sur le site public.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={uploading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!selectedFile || uploading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {uploading ? "Transfert..." : "Sauvegarder"}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}